// Vue-related and Decorators
import { Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';

// Models
import type { CompanySetting } from '@/models/companySetting';
import type { DynamicField } from '@/models/dynamicField';
import type { ExtendedTrade } from '@/models/trade';
import type { Phase } from '@/models/phase';
import type { PMPhase } from '@/models/pmphase';
import type { PMSubPhase, PMSubPhaseGroup } from '@/models/pmsubphase';
import type { Project } from '@/models/project';
import type { ProjectUser } from '@/models/projectUser';
import type { Topic } from '@/models/topic';
import type { XProcess } from '@/models/xprocess';
import type { XSubProcess, XSubProcessGroup } from '@/models/xsubprocess';

// Helpers, Utilities, and Miscellaneous
import { createAppliedLessonsFilter } from '@/helpers/createAppliedLessonsFilter';
import { getCombinedProjectUsers } from '@/helpers/getCombinedProjectUsers';
import { isDesktop } from '@/helpers/isDesktop';
import { showErrorNotification } from '@/helpers/showErrorNotification';
import { toggleModal } from '@/helpers/modal';
import moment from 'moment';
import { phaseWebservice } from '@/webservices/phase';
import { topicWebservice } from '@/webservices/topic';
import { findCombinedUser } from '@/helpers/findCombinedUser';
import {
    filterAppliedLessonsResolved,
    ResolvedEnum,
} from '@/helpers/filter/appliedLessons/filterAppliedLessonsResolved';
import { CheckedEnum, filterAppliedLessonsChecked } from '@/helpers/filter/appliedLessons/filterAppliedLessonsChecked';
import type { AppliedLessonsFilter } from '@/helpers/AppliedLessonsFilter';

// noinspection BadExpressionStatementJS
export default class ProjectViewListBase extends Vue {
    protected get pmSubPhasesGrouped(): PMSubPhaseGroup[] {
        const subPhasesGroup: PMSubPhaseGroup[] = [];

        this.selectedPmPhases.forEach((phase) => {
            for (const subPhase of phase.sub_phases) {
                const i = subPhasesGroup.findIndex((subPhaseGroup) => subPhaseGroup.title === subPhase.title);

                if (i === -1) {
                    const subPhaseGroup: PMSubPhaseGroup = {
                        title: subPhase.title,
                        sub_phases_ids: [subPhase.id],
                        company_id: subPhase.company_id,
                        order: subPhase.order ? subPhase.order : 1,
                    };
                    subPhasesGroup.push(subPhaseGroup);
                } else {
                    subPhasesGroup[i].sub_phases_ids.push(subPhase.id);
                }
            }
        });

        return subPhasesGroup;
    }

    protected get pmSubPhases(): PMSubPhase[] {
        const subPhases: PMSubPhase[] = [];

        this.selectedPmPhases.forEach((phase) => {
            for (const subPhase of phase.sub_phases) {
                subPhases.push(subPhase);
            }
        });
        return subPhases;
    }

    protected get xSubProcessesGrouped(): XSubProcessGroup[] {
        const subProcessesGroup: XSubProcessGroup[] = [];

        this.selectedXProcesses.forEach((phase) => {
            for (const subProcess of phase.x_sub_processes) {
                const i = subProcessesGroup.findIndex((subProcessGroup) => subProcessGroup.title === subProcess.title);

                if (i === -1) {
                    const subProcessGroup: XSubProcessGroup = {
                        title: subProcess.title,
                        sub_processes_ids: [subProcess.id],
                        company_id: subProcess.company_id,
                        order: subProcess.order ? subProcess.order : 1,
                    };
                    subProcessesGroup.push(subProcessGroup);
                } else {
                    subProcessesGroup[i].sub_processes_ids.push(subProcess.id);
                }
            }
        });

        return subProcessesGroup;
    }

    // noinspection JSUnusedGlobalSymbols
    protected get xSubProcess(): XSubProcess[] {
        const subProcesses: XSubProcess[] = [];

        this.selectedXProcesses.forEach((process) => {
            for (const subProcess of process.x_sub_processes) {
                subProcesses.push(subProcess);
            }
        });
        return subProcesses;
    }

    protected get users() {
        return getCombinedProjectUsers(this.project) as ProjectUser[];
    }

    protected get pmPhases() {
        return this.preparePmphases(this.project.pmphases);
    }

    protected get xProcesses() {
        return this.prepareXProcesses(this.project.xprocesses);
    }

    protected get selectedPmSubPhases(): PMSubPhase[] {
        if (this.selectedPmSubPhasesGrouped && this.selectedPmSubPhasesGrouped.length > 0) {
            const selectedIds: number[] = this.selectedPmSubPhasesGrouped.reduce(
                (acc: number[], group) => acc.concat(group.sub_phases_ids),
                []
            );
            return this.pmSubPhases.filter((subPhase) => selectedIds.includes(subPhase.id));
        }
        return [];
    }

    protected get selectedXSubProcess(): XSubProcess[] {
        if (this.selectedXSubProcessesGrouped && this.selectedXSubProcessesGrouped.length > 0) {
            const selectedIds: number[] = this.selectedXSubProcessesGrouped.reduce(
                (acc: number[], group) => acc.concat(group.sub_processes_ids),
                []
            );
            return this.xSubProcesses.filter((subPhase) => selectedIds.includes(subPhase.id));
        }
        return [];
    }

    public tableColumns: AppliedLessonsFilter | null = null;
    @Prop({
        required: true,
    })
    protected project: Project;

    protected selectedPmPhases: PMPhase[] = [];

    @Getter('account/companySetting') protected companySetting: CompanySetting;
    protected isDesktop = isDesktop();

    protected filterResolved = ResolvedEnum.NotResolved;
    protected filterChecked = CheckedEnum.All;
    protected selectedDynamicFields: DynamicField[] = [];

    protected selectedTrades: ExtendedTrade[] = [];
    protected selectedPhases: Phase[] = [];
    protected selectedTopics: Topic[] = [];
    protected phases: Phase[] = [];
    protected topics: Topic[] = [];

    protected selectedXProcesses: XProcess[] = [];
    protected selectedXSubProcessesGrouped: XSubProcessGroup[] = [];

    protected xSubProcesses: XSubProcess[] = [];
    protected selectedPmSubPhasesGrouped: PMSubPhaseGroup[] = [];

    protected toggleModal = toggleModal;
    protected showErrorNotification = showErrorNotification;

    @Getter('company/dynamicFields') protected dynamicFields: DynamicField[];
    @Action('company/fetchAllDynamicFields') protected fetchAllDynamicFields: any;

    @Action('project/refreshSelectedProject') protected refreshSelectedProject: () => Promise<void>;
    @Getter('utils/currentLocale') protected currentLocale: string;

    protected loadPhases() {
        phaseWebservice.loadForProjectWithWorksheets(this.project.id).then((response) => {
            this.phases = response.data;
            this.selectedPhases = this.phases.filter((phase: Phase) => phase.worksheets!.length > 0) as Phase[];
        });
    }

    protected loadTopics() {
        topicWebservice
            .load()
            .then((response) => {
                const filteredTopics = response.data.filter((topic: Topic) => {
                    return topic.title !== 'Nicht zugeordnete Lehren';
                });

                this.topics = filteredTopics.sort((a: Topic, b: Topic) => {
                    return a.title.localeCompare(b.title);
                });
            })
            .catch(() => {
                showErrorNotification('');
            });
    }

    protected hasMatchingDynamicField(lesson) {
        let display = false;
        if (
            this.selectedDynamicFields.length === 0 ||
            (lesson.dynamic_field && this.selectedDynamicFields.some((df) => df.id === lesson.dynamic_field.id))
        ) {
            display = true;
        }

        if (lesson.sub_lessons) {
            lesson.sub_lessons = lesson.sub_lessons.filter((subLesson) => this.hasMatchingDynamicField(subLesson));
        }
        return display || (lesson.sub_lessons && lesson.sub_lessons.length > 0);
    }

    protected preparePmphases(pmPhases) {
        // Make vue watch the following filters. (Yeah...that's not especially nice, but it works)
        // @other devs, I am open for other approaches.
        // tslint:disable-next-line:no-unused-expression
        this.filterResolved;
        // tslint:disable-next-line:no-unused-expression
        this.filterChecked;
        // tslint:disable-next-line:no-unused-expression
        this.selectedDynamicFields;

        const clonedPMPhases = JSON.parse(JSON.stringify(pmPhases));

        return clonedPMPhases
            .filter((pmphase) => {
                return (
                    pmphase.worksheet &&
                    pmphase.worksheet.pm_applied_lessons.length > 0 &&
                    this.selectedPmPhases.some((selectedPmPhase) => selectedPmPhase.id === pmphase.id)
                );
            })
            .map((pmphase) => {
                pmphase.worksheet.pm_applied_lessons = pmphase.worksheet.pm_applied_lessons
                    // Map Responsible Users & Date Objects to applied lesson
                    // Only Resolved / Open
                    .filter((appliedLesson) => {
                        return filterAppliedLessonsResolved(appliedLesson, this.filterResolved);
                    })
                    .filter((appliedLesson) => {
                        return filterAppliedLessonsChecked(appliedLesson, this.filterChecked);
                    })
                    // Dynamic Field
                    .filter((appliedLesson) => {
                        if (!this.tableColumns || !this.tableColumns.dynamic_field.display) {
                            return true;
                        }
                        // Check the applied lesson and its sub-lessons
                        return this.hasMatchingDynamicField(appliedLesson);
                    })
                    // Selected Sub Phase
                    .filter((appliedLesson) => {
                        return (
                            this.selectedPmSubPhases.length === 0 ||
                            (appliedLesson.lesson.sub_phase &&
                                this.selectedPmSubPhases.some((sp) => sp.id === appliedLesson.lesson.sub_phase.id))
                        );
                    });
                pmphase.worksheet.pm_applied_lessons = this.mapUsersRec(pmphase.worksheet.pm_applied_lessons);
                return pmphase;
            })
            .filter((pmphase) => {
                return pmphase.worksheet.pm_applied_lessons.length > 0;
            });
    }

    protected prepareXProcesses(xProcess) {
        // Make vue watch the following filters. (Yeah...that's not especially nice, but it works)
        // @other devs, I am open for other approaches.
        // tslint:disable-next-line:no-unused-expression
        this.filterResolved;
        // tslint:disable-next-line:no-unused-expression
        this.filterChecked;
        // tslint:disable-next-line:no-unused-expression
        this.selectedDynamicFields;

        const clonedXProcess = JSON.parse(JSON.stringify(xProcess));

        return clonedXProcess
            .filter((xprocess) => {
                return xprocess.x_worksheet && xprocess.x_worksheet.x_applied_lessons.length > 0;
            })
            .map((xprocess) => {
                xprocess.x_worksheet.x_applied_lessons = xprocess.x_worksheet.x_applied_lessons
                    // Map Responsible Users & Date Objects to applied lesson
                    // Only Resolved / Open
                    .filter((appliedLesson) => {
                        return filterAppliedLessonsResolved(appliedLesson, this.filterResolved);
                    })
                    .filter((appliedLesson) => {
                        return filterAppliedLessonsChecked(appliedLesson, this.filterChecked);
                    })
                    // Dynamic Field
                    .filter((appliedLesson) => {
                        if (!this.tableColumns || !this.tableColumns.dynamic_field.display) {
                            return true;
                        }
                        // Check the applied lesson and its sub-lessons
                        return this.hasMatchingDynamicField(appliedLesson);
                    })
                    // Selected Sub Processes
                    .filter((appliedLesson) => {
                        return (
                            this.selectedXSubProcess.length === 0 ||
                            (appliedLesson.lesson.x_sub_process &&
                                this.selectedXSubProcess.some((sp) => sp.id === appliedLesson.lesson.x_sub_process.id))
                        );
                    });
                xprocess.x_worksheet.x_applied_lessons = this.mapUsersRec(xprocess.x_worksheet.x_applied_lessons);
                return xprocess;
            })
            .filter((xprocess) => {
                return xprocess.x_worksheet.x_applied_lessons.length > 0;
            });
    }

    protected mapUsersRec(lessons: any, mapped: [] = []) {
        mapped = lessons.map((lesson) => {
            lesson.responsible_user = findCombinedUser(this.users, lesson);
            lesson.contacttill = lesson.contacttill ? moment(lesson.contacttill).toDate() : null;
            if (lesson.sub_lessons) {
                lesson.sub_lessons = this.mapUsersRec(lesson.sub_lessons, mapped);
            }
            return lesson;
        });
        return mapped;
    }
}
